import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import DatabaseIcon from '@atlaskit/icon/core/database';
import LinkIcon from '@atlaskit/icon/core/link';
import ArrowsDiagonalUpRightDownLeftIcon from '@atlaskit/icon-lab/core/arrows-diagonal-up-right-down-left';
import { FieldTypeCategory, getFieldTypes } from '@databases/fields';
import { messages } from '@databases/intl/messages';
import { DatabaseFieldType } from '@databases/types/database';
import { useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { addFieldMenuMessages } from './messages';
import type { FieldTypeEntry } from './types';

const isFieldTypeVisible = (type: DatabaseFieldType, showTypes?: DatabaseFieldType[]) =>
  !showTypes || showTypes.includes(type);

const basicFieldTypeOrder = [
  DatabaseFieldType.TEXT,
  DatabaseFieldType.SELECT,
  DatabaseFieldType.ATTACHMENT,
  DatabaseFieldType.USER,
  DatabaseFieldType.NUMBER,
  DatabaseFieldType.HYPERLINK,
  DatabaseFieldType.DATE
];

const confluenceFieldTypeOrder = [
  DatabaseFieldType.PAGE_LINK,
  DatabaseFieldType.ENTRY_LINK,
  DatabaseFieldType.PAGE_STATUS,
  DatabaseFieldType.BACKLINK,
  DatabaseFieldType.PAGE_LABELS,
  DatabaseFieldType.ENTRY_DETAILS,
  DatabaseFieldType.PAGE_DETAILS
];

const jiraFieldTypeOrder = [
  DatabaseFieldType.JIRA_ISSUE,
  DatabaseFieldType.JIRA_ISSUE_DETAILS
];

const confluenceIconOverrides: Partial<
  Record<DatabaseFieldType, FieldTypeEntry['Icon']>
> = {
  [DatabaseFieldType.PAGE_LINK]: LinkIcon,
  [DatabaseFieldType.PAGE_STATUS]: ArrowsDiagonalUpRightDownLeftIcon,
  [DatabaseFieldType.PAGE_LABELS]: ArrowsDiagonalUpRightDownLeftIcon,
  [DatabaseFieldType.PAGE_DETAILS]: ArrowUpRightIcon,
  [DatabaseFieldType.ENTRY_DETAILS]: ArrowUpRightIcon
};

const jiraIconOverrides: Partial<Record<DatabaseFieldType, FieldTypeEntry['Icon']>> = {
  [DatabaseFieldType.JIRA_ISSUE]: LinkIcon,
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: DatabaseIcon
};

export const useFieldTypeEntries = (showTypes: DatabaseFieldType[] | undefined) => {
  const { formatMessage } = useIntl();

  const confluenceLabelOverrides = useMemo<Partial<Record<DatabaseFieldType, string>>>(
    () => ({
      [DatabaseFieldType.PAGE_LINK]: formatMessage(addFieldMenuMessages.confluenceLink),
      [DatabaseFieldType.PAGE_STATUS]: formatMessage(
        addFieldMenuMessages.confluenceStatus
      ),
      [DatabaseFieldType.PAGE_LABELS]: formatMessage(
        addFieldMenuMessages.confluenceLabel
      ),
      [DatabaseFieldType.PAGE_DETAILS]: formatMessage(
        addFieldMenuMessages.confluenceMoreDetails
      ),
      [DatabaseFieldType.ENTRY_DETAILS]: formatMessage(addFieldMenuMessages.entryDetail)
    }),
    [formatMessage]
  );

  const jiraLabelOverrides = useMemo<Partial<Record<DatabaseFieldType, string>>>(
    () => ({
      [DatabaseFieldType.JIRA_ISSUE]: formatMessage(addFieldMenuMessages.jiraLink),
      [DatabaseFieldType.JIRA_ISSUE_DETAILS]: formatMessage(
        addFieldMenuMessages.jiraDetail
      )
    }),
    [formatMessage]
  );

  return useMemo(() => {
    const fieldTypes = getFieldTypes();
    const fieldTypeEntries = Object.entries(fieldTypes) as Array<
      [DatabaseFieldType, (typeof fieldTypes)[DatabaseFieldType]]
    >;

    const entries = fieldTypeEntries
      .filter(([type]) => isFieldTypeVisible(type, showTypes))
      .map(([type, fieldType]) => ({
        type,
        label:
          confluenceLabelOverrides[type] ??
          jiraLabelOverrides[type] ??
          formatMessage(fieldType.label),
        labelDescriptor: fieldType.label,
        Icon: confluenceIconOverrides[type] ?? jiraIconOverrides[type],
        category: fieldType.category
      }));

    const basicEntries = entries.filter(
      ({ category }) => category === FieldTypeCategory.BASIC
    );

    return {
      basic: basicFieldTypeOrder.flatMap((type) =>
        basicEntries.filter((entry) => entry.type === type)
      ),
      confluence: confluenceFieldTypeOrder
        .flatMap((type) => entries.filter((entry) => entry.type === type))
        .filter(
          ({ category }) =>
            category === FieldTypeCategory.PAGE ||
            category === FieldTypeCategory.OTHER_DATABASES
        ),
      jira: jiraFieldTypeOrder
        .flatMap((type) => entries.filter((entry) => entry.type === type))
        .filter(({ category }) => category === FieldTypeCategory.JIRA),
      jiraLabel: formatMessage(messages['field-type.category-jira']),
      basicLabel: formatMessage(addFieldMenuMessages.basicFieldTypesSection),
      confluenceLabel: formatMessage(addFieldMenuMessages.confluenceSection)
    };
  }, [confluenceLabelOverrides, formatMessage, jiraLabelOverrides, showTypes]);
};
